import React, { FC } from 'react';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@mui/material';
import Button from '@app/components/Button';
import NakedDropdown from '@app/components/inputs/NakedDropdown';
import DropdownItem from '@app/components/inputs/DropdownItem';
import COUNTRIES, { getCountryName } from '@app/domain/countries';
import { formatCountryDisplay } from '../NewVerificationTab.utils';
import { useTranslation } from 'react-i18next';
import { DataPointType } from '../NewVerificationTab.data';
import { MODAL_LABELS } from '../NewVerificationTab.constants';
import { LegalEntityIdType } from '@mortee/domain/vaildatedPayeeManagement';
import Loadable from '@app/utils/Loadable';

interface EditModalProps {
  isOpen: boolean;
  currentValue: string;
  dataPointType: DataPointType | null;
  dataPoint?: React.ReactNode;
  vendorValue?: string;
  evidenceValue?: string;
  evidenceItems?: Array<{ value: string; source?: string; evidenceType?: string }>;
  columnType: 'manualProcess' | 'finalReview' | null;
  /** Background color to tint the value input with, matching the related cell's status color. */
  inputBackgroundColor?: string;
  taxIdTypes?: Loadable<LegalEntityIdType[]>;
  onSave: (value: string | undefined) => void;
  onCancel: () => void;
  onPresetClick: (presetValue: string) => void;
}

export const EditModal: FC<EditModalProps> = ({
  isOpen,
  currentValue,
  dataPointType,
  dataPoint,
  vendorValue,
  evidenceValue,
  evidenceItems,
  columnType,
  inputBackgroundColor,
  taxIdTypes,
  onSave,
  onCancel,
  onPresetClick,
}) => {
  const { t } = useTranslation();
  const isLegalIdentifierType = dataPointType === 'legalIdentifiers';
  const isCountryType = dataPointType === 'country' || dataPointType === 'accountCountry';

  // Determine if this field should show the "Marked as Not Relevant" checkbox
  const dataPointString = typeof dataPoint === 'string' ? dataPoint : String(dataPoint || '');
  const isAdditionalName =
    dataPointType === 'name' && (dataPointString === 'Additional Name' || dataPointString.includes('Additional Name'));
  const isAdditionalAddress =
    dataPointType === 'address' && (dataPointString === 'Additional Address' || dataPointString.includes('Additional Address'));
  const isAdditionalBankCode =
    dataPointType === 'bankCode' && dataPointString !== 'Bank Code' && dataPointString.includes('Bank Code');

  // Show "Marked as Not Relevant" for both Manual Processing and Final Review, for: Additional Name,
  // Additional Address, Legal Identifiers, Bank Code, Additional Bank Code, Branch Code, Swift Code,
  // Account Number, IBAN, For Further Credit
  const shouldShowNotRelevantCheckbox =
    (columnType === 'manualProcess' || columnType === 'finalReview') &&
    ((dataPointType === 'bankCode' && (dataPointString === 'Bank Code' || isAdditionalBankCode)) ||
      dataPointType === 'branchCode' ||
      dataPointType === 'swiftCode' ||
      dataPointType === 'accountNumber' ||
      dataPointType === 'iban' ||
      dataPointType === 'forFurtherCredit' ||
      isAdditionalName ||
      isAdditionalAddress);

  const [isNotRelevant, setIsNotRelevant] = React.useState(false);

  // Parse taxId value: format is "value - type (country)" or "value - type (country name)" or just "value"
  const parseTaxIdValue = (val: string): { value: string; type: string; country: string } => {
    if (!val) return { value: '', type: '', country: '' };

    const match = val.match(/^(.+?)\s*-\s*(.+?)\s*\((.+?)\)$/);
    if (match) {
      const countryValue = match[3].trim();
      // Try to find country code from country name, or use the value as-is if it's already a code
      let countryCode = countryValue;
      const countryEntry = Object.entries(COUNTRIES).find(
        ([code, country]) => country.name === countryValue || code === countryValue,
      );
      if (countryEntry) {
        countryCode = countryEntry[0];
      }

      return {
        value: match[1].trim(),
        type: match[2].trim(),
        country: countryCode,
      };
    }
    // If no match, assume it's just the value
    return { value: val.trim(), type: '', country: '' };
  };

  const parsedLegalId = isLegalIdentifierType ? parseTaxIdValue(currentValue) : null;

  const [value, setValue] = React.useState(currentValue);
  const [taxIdValue, setTaxIdValue] = React.useState(parsedLegalId?.value || '');
  const [taxIdType, setTaxIdType] = React.useState(parsedLegalId?.type || '');
  const [taxIdCountry, setTaxIdCountry] = React.useState(parsedLegalId?.country || '');
  const [dropdownResetKey, setDropdownResetKey] = React.useState(0);
  const taxIdCountryDropdownRef = React.useRef<HTMLDivElement>(null);
  const countryDropdownRef = React.useRef<HTMLDivElement>(null);

  // Reset dropdowns when modal opens and ensure they're closed
  React.useEffect(() => {
    if (isOpen) {
      setDropdownResetKey((prev) => prev + 1);
      // Blur any inputs in the dropdown containers to prevent them from opening
      setTimeout(() => {
        [taxIdCountryDropdownRef.current, countryDropdownRef.current].forEach((ref) => {
          if (ref) {
            const input = ref.querySelector('input');
            if (input && document.activeElement === input) {
              input.blur();
            }
          }
        });
      }, 50);
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (isLegalIdentifierType) {
      const parsed = parseTaxIdValue(currentValue);
      setTaxIdValue(parsed.value);
      setTaxIdType(parsed.type);
      setTaxIdCountry(parsed.country);
    } else if (isCountryType) {
      // Handle "CODE - Name", plain country name, or bare code
      const codeFromFormatted = currentValue?.match(/^([A-Z]{2,3})\s*-\s*.+$/)?.[1];
      const countryCode =
        codeFromFormatted ||
        Object.entries(COUNTRIES).find(
          ([code, country]) => country.name === currentValue || getCountryName(code) === currentValue || code === currentValue,
        )?.[0] ||
        currentValue;
      setValue(countryCode);
    } else {
      setValue(currentValue);
    }
    // Check if value is empty (which represents undefined) for fields that support "Not Relevant"
    setIsNotRelevant(shouldShowNotRelevantCheckbox && (currentValue === undefined || currentValue === ''));
  }, [currentValue, isCountryType, shouldShowNotRelevantCheckbox, isLegalIdentifierType]);

  const showPresets =
    (columnType === 'manualProcess' || columnType === 'finalReview') &&
    (dataPointType === 'name' ||
      dataPointType === 'address' ||
      dataPointType === 'country' ||
      dataPointType === 'accountCountry' ||
      dataPointType === 'legalIdentifiers' ||
      dataPointType === 'bankName' ||
      dataPointType === 'bankCode' ||
      dataPointType === 'branchCode' ||
      dataPointType === 'iban' ||
      dataPointType === 'swiftCode') &&
    (vendorValue || evidenceValue);

  const handleSave = (): void => {
    // If "Not Relevant" is checked, save undefined
    if (shouldShowNotRelevantCheckbox && isNotRelevant) {
      onSave(undefined);
      return;
    }

    if (isLegalIdentifierType) {
      // Format: "value - type (country name)"
      let formattedValue = taxIdValue;
      if (taxIdType) {
        formattedValue += ` - ${taxIdType}`;
      }
      if (taxIdCountry) {
        const countryName = getCountryName(taxIdCountry) || COUNTRIES[taxIdCountry]?.name || taxIdCountry;
        formattedValue += ` (${countryName})`;
      }
      onSave(formattedValue);
    } else if (isCountryType) {
      onSave(value ? formatCountryDisplay(value) : '');
    } else {
      onSave(value);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open): void => {
        if (!open) {
          onCancel();
        }
      }}
    >
      <DialogContent
        className='overflow-hidden max-h-[600px]'
        style={{
          fontFamily: "'SF Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
        }}
      >
        <div className='py-4 min-w-0 max-w-full overflow-hidden space-y-4'>
          {isLegalIdentifierType ? (
            <>
              <div>
                <label className='block text-sm font-medium mb-2'>Value</label>
                <Input
                  type='text'
                  value={taxIdValue}
                  onChange={(e): void => setTaxIdValue(e.target.value)}
                  autoFocus
                  placeholder='Enter value'
                  disabled={shouldShowNotRelevantCheckbox && isNotRelevant}
                  style={inputBackgroundColor ? { backgroundColor: inputBackgroundColor } : undefined}
                />
              </div>
              <div>
                <label className='block text-sm font-medium mb-2'>Type</label>
                <div onClick={(e): void => e.stopPropagation()}>
                  {taxIdTypes?.resolve(
                    (types) => (
                      <NakedDropdown
                        accessibilityLabel='Tax ID Type'
                        name='edit-taxid-type-dropdown'
                        dataTestId='edit-taxid-type-dropdown'
                        placeholder='Select a type...'
                        value={taxIdType ? types.find((t) => t.name === taxIdType)?.name || taxIdType : undefined}
                        onChange={(newValue): void => {
                          if (newValue !== undefined && newValue !== null) {
                            setTaxIdType(newValue);
                          }
                        }}
                        isSearchable
                        disablePortal={true}
                      >
                        {types.map((type) => (
                          <DropdownItem key={type.id} value={type.name} textWhenSelected={type.name} keywords={[type.name]}>
                            <div>{type.name}</div>
                          </DropdownItem>
                        ))}
                      </NakedDropdown>
                    ),
                    () => (
                      <NakedDropdown
                        accessibilityLabel='Tax ID Type'
                        name='edit-taxid-type-dropdown'
                        dataTestId='edit-taxid-type-dropdown'
                        placeholder='Loading types...'
                        disabled
                        children={[]}
                      />
                    ),
                  )}
                </div>
              </div>
              <div>
                <label className='block text-sm font-medium mb-2'>Country</label>
                <div ref={taxIdCountryDropdownRef} onClick={(e): void => e.stopPropagation()}>
                  <NakedDropdown
                    key={`taxid-country-${dropdownResetKey}`}
                    accessibilityLabel={t('general.accessibility.country')}
                    name='edit-taxid-country-dropdown'
                    dataTestId='edit-taxid-country-dropdown'
                    placeholder='Select a country...'
                    value={taxIdCountry || undefined}
                    onChange={(newValue): void => {
                      if (newValue !== undefined && newValue !== null) {
                        setTaxIdCountry(newValue);
                      }
                    }}
                    isSearchable
                    disablePortal={true}
                  >
                    {Object.entries(COUNTRIES)
                      .sort(([, a], [, b]) => a.name.localeCompare(b.name))
                      .map(([countryCode, country]) => {
                        const countryName = getCountryName(countryCode) || country.name;
                        const display = `${countryCode} - ${countryName}`;
                        return (
                          <DropdownItem
                            key={countryCode}
                            value={countryCode}
                            textWhenSelected={display}
                            keywords={[countryName, countryCode]}
                          >
                            <div>{display}</div>
                          </DropdownItem>
                        );
                      })}
                  </NakedDropdown>
                </div>
              </div>
            </>
          ) : (
            <>
              {!isCountryType && <label className='block text-sm font-medium mb-2'>{MODAL_LABELS.VALUE}</label>}
              {isCountryType ? (
                <div
                  ref={countryDropdownRef}
                  onClick={(e): void => e.stopPropagation()}
                  className='border border-gray-300 rounded-md p-2'
                >
                  <NakedDropdown
                    key={`country-${dropdownResetKey}`}
                    accessibilityLabel={t('general.accessibility.country')}
                    name='edit-country-dropdown'
                    dataTestId='edit-country-dropdown'
                    placeholder='Select a country...'
                    value={value || undefined}
                    onChange={(newValue): void => {
                      if (newValue !== undefined && newValue !== null) {
                        setValue(newValue);
                      }
                    }}
                    isSearchable
                    disablePortal={true}
                  >
                    {Object.entries(COUNTRIES)
                      .sort(([, a], [, b]) => a.name.localeCompare(b.name))
                      .map(([countryCode, country]) => {
                        const countryName = getCountryName(countryCode) || country.name;
                        const display = `${countryCode} - ${countryName}`;
                        return (
                          <DropdownItem
                            key={countryCode}
                            value={countryCode}
                            textWhenSelected={display}
                            keywords={[countryName, countryCode]}
                          >
                            <div>{display}</div>
                          </DropdownItem>
                        );
                      })}
                  </NakedDropdown>
                </div>
              ) : (
                <>
                  <Input
                    type='text'
                    value={value}
                    onChange={(e): void => setValue(e.target.value)}
                    autoFocus
                    disabled={shouldShowNotRelevantCheckbox && isNotRelevant}
                    style={inputBackgroundColor ? { backgroundColor: inputBackgroundColor } : undefined}
                  />
                  {shouldShowNotRelevantCheckbox && (
                    <div
                      className='flex items-center mt-2'
                      onClick={(): void => setIsNotRelevant(!isNotRelevant)}
                      style={{ cursor: 'pointer', width: 'fit-content' }}
                    >
                      <Checkbox
                        id='not-relevant-checkbox'
                        checked={isNotRelevant}
                        onClick={(e): void => e.stopPropagation()}
                        onChange={(e): void => setIsNotRelevant(e.target.checked)}
                      />
                      <label htmlFor='not-relevant-checkbox' style={{ cursor: 'pointer' }}>
                        Not Relevant
                      </label>
                    </div>
                  )}
                </>
              )}
            </>
          )}
          {showPresets && (
            <div className='mt-4 space-y-2 min-w-0'>
              <label className='block text-xs font-medium text-gray-600 mb-2'>{MODAL_LABELS.PRESETS}:</label>
              <div className='flex flex-col gap-2 min-w-0'>
                {vendorValue && (
                  <Button
                    id='preset-vendor'
                    appearance='outline'
                    colorScheme='primary'
                    onClick={(): void => {
                      if (isLegalIdentifierType) {
                        const parsed = parseTaxIdValue(vendorValue);
                        setTaxIdValue(parsed.value);
                        setTaxIdType(parsed.type);
                        setTaxIdCountry(parsed.country);
                      } else if (
                        dataPointType === 'bankName' ||
                        dataPointType === 'bankCode' ||
                        dataPointType === 'branchCode' ||
                        dataPointType === 'iban' ||
                        dataPointType === 'swiftCode'
                      ) {
                        onPresetClick(vendorValue);
                      } else if (isCountryType) {
                        // Convert country name to code for country fields
                        const countryCode =
                          Object.entries(COUNTRIES).find(
                            ([code, country]) => country.name === vendorValue || getCountryName(code) === vendorValue,
                          )?.[0] || vendorValue;
                        onPresetClick(countryCode);
                      } else {
                        onPresetClick(vendorValue);
                      }
                    }}
                    className='w-full text-left min-w-0 overflow-hidden'
                  >
                    <span className='block break-words min-w-0'>
                      <span className='font-medium'>{MODAL_LABELS.VENDOR_REGISTRATION}</span>{' '}
                      {isCountryType ? getCountryName(vendorValue) || COUNTRIES[vendorValue]?.name || vendorValue : vendorValue}
                    </span>
                  </Button>
                )}
                {evidenceItems && evidenceItems.length > 0
                  ? evidenceItems.map((item, idx) => {
                      const displayValue = item.value || '';
                      const itemLabel = [item.source, item.evidenceType].filter(Boolean).join(' · ');
                      return (
                        <Button
                          key={`preset-evidence-${idx}`}
                          id={`preset-evidence-${idx}`}
                          appearance='outline'
                          colorScheme='primary'
                          onClick={(): void => {
                            if (isLegalIdentifierType) {
                              const parsed = parseTaxIdValue(displayValue);
                              setTaxIdValue(parsed.value);
                              setTaxIdType(parsed.type);
                              setTaxIdCountry(parsed.country);
                            } else if (isCountryType) {
                              const countryCode =
                                Object.entries(COUNTRIES).find(
                                  ([code, country]) => country.name === displayValue || getCountryName(code) === displayValue,
                                )?.[0] || displayValue;
                              onPresetClick(countryCode);
                            } else {
                              onPresetClick(displayValue);
                            }
                          }}
                          className='w-full text-left min-w-0 overflow-hidden'
                        >
                          <span className='block break-words min-w-0'>
                            <span className='font-medium'>{MODAL_LABELS.EVIDENCE_ENRICHMENT}</span> {displayValue}
                            {itemLabel && <span className='text-xs text-gray-500 ml-1'>({itemLabel})</span>}
                          </span>
                        </Button>
                      );
                    })
                  : evidenceValue && (
                      <Button
                        id='preset-evidence'
                        appearance='outline'
                        colorScheme='primary'
                        onClick={(): void => {
                          if (isLegalIdentifierType) {
                            const parsed = parseTaxIdValue(evidenceValue);
                            setTaxIdValue(parsed.value);
                            setTaxIdType(parsed.type);
                            setTaxIdCountry(parsed.country);
                          } else if (isCountryType) {
                            const countryCode =
                              Object.entries(COUNTRIES).find(
                                ([code, country]) => country.name === evidenceValue || getCountryName(code) === evidenceValue,
                              )?.[0] || evidenceValue;
                            onPresetClick(countryCode);
                          } else {
                            onPresetClick(evidenceValue);
                          }
                        }}
                        className='w-full text-left min-w-0 overflow-hidden'
                      >
                        <span className='block break-words min-w-0'>
                          <span className='font-medium'>{MODAL_LABELS.EVIDENCE_ENRICHMENT}</span>{' '}
                          {isCountryType
                            ? getCountryName(evidenceValue) || COUNTRIES[evidenceValue]?.name || evidenceValue
                            : evidenceValue}
                        </span>
                      </Button>
                    )}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button id='modal-cancel' appearance='outline' colorScheme='primary' onClick={onCancel}>
            {MODAL_LABELS.CANCEL}
          </Button>
          <Button id='modal-save' colorScheme='primary' onClick={handleSave}>
            {MODAL_LABELS.SAVE}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
